import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  EMPTY_TOTALS,
  bump as bumpCart,
  linesForReorder,
  priceCart,
  setQty as setCartQty,
  type CartLines,
  type Channel,
  type LatLng,
  type Order,
  type Product,
  type Totals,
  type CancelReason,
} from "@moomoo/core";
import { ApiError, api, type AppConfig, type Quote, type Slot } from "./api.js";

/**
 * App state.
 *
 * Deliberately plain React: this app has one screen's worth of state and a
 * cart. A state library here would be weight without benefit.
 *
 * The cart, the confirmed pin and the last order id survive a reload, because
 * on a phone the browser is killed constantly and losing a half-built basket
 * is the fastest way to lose an order.
 */

const STORAGE_KEY = "moomoo.cart.v2";

interface Persisted {
  lines: CartLines;
  location?: LatLng;
  channel: Channel;
  wholesaleCode?: string;
  lastOrderId?: string;
}

function isLatLng(v: unknown): v is LatLng {
  return (
    typeof v === "object" && v !== null &&
    Number.isFinite((v as LatLng).lat) && Number.isFinite((v as LatLng).lng)
  );
}

function load(): Persisted {
  const fallback: Persisted = { lines: {}, channel: "retail" };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    return {
      lines: typeof parsed.lines === "object" && parsed.lines ? parsed.lines : {},
      ...(isLatLng(parsed.location) ? { location: parsed.location } : {}),
      channel: parsed.channel === "wholesale" ? "wholesale" : "retail",
      ...(typeof parsed.wholesaleCode === "string" ? { wholesaleCode: parsed.wholesaleCode } : {}),
      ...(typeof parsed.lastOrderId === "string" ? { lastOrderId: parsed.lastOrderId } : {}),
    };
  } catch {
    // a corrupt or blocked store must never stop the shop from opening
    return fallback;
  }
}

function save(state: Persisted): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* private mode, quota, or blocked storage: carry on without persistence */
  }
}

export type Screen = "shop" | "cart" | "checkout" | "track";

/** The order id in the address bar, so the page can be reloaded or shared. */
function orderIdFromHash(): string | undefined {
  const m = /^#order\/([0-9a-f-]{36})$/.exec(typeof window === "undefined" ? "" : window.location.hash);
  return m?.[1];
}

export interface AppState {
  ready: boolean;
  error: string | undefined;
  stale: boolean;
  products: readonly Product[];
  config: AppConfig | undefined;
  slot: Slot | undefined;
  lines: CartLines;
  channel: Channel;
  wholesaleCode: string | undefined;
  location: LatLng | undefined;
  quote: Quote | undefined;
  quoteError: string | undefined;
  quoting: boolean;
  totals: Totals;
  screen: Screen;
  order: Order | undefined;
  clampNotice: string | undefined;
}

export function useApp() {
  const [products, setProducts] = useState<readonly Product[]>([]);
  const [config, setConfig] = useState<AppConfig | undefined>();
  const [slot, setSlot] = useState<Slot | undefined>();
  const [stale, setStale] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [screen, setScreen] = useState<Screen>("shop");
  const [order, setOrder] = useState<Order | undefined>();
  const [clampNotice, setClampNotice] = useState<string | undefined>();

  const persisted = useRef<Persisted>(load());
  const [lines, setLines] = useState<CartLines>(persisted.current.lines);
  const [channel, setChannel] = useState<Channel>(persisted.current.channel);
  const [location, setLocation] = useState<LatLng | undefined>(persisted.current.location);
  const [wholesaleCode, setWholesaleCode] = useState<string | undefined>(persisted.current.wholesaleCode);
  const [quote, setQuote] = useState<Quote | undefined>();
  const [quoteError, setQuoteError] = useState<string | undefined>();
  const [quoting, setQuoting] = useState(false);

  useEffect(() => {
    save({
      lines,
      channel,
      ...(location ? { location } : {}),
      ...(wholesaleCode ? { wholesaleCode } : {}),
      ...(order ? { lastOrderId: order.id } : {}),
    });
  }, [lines, location, channel, wholesaleCode, order]);

  const refresh = useCallback(async () => {
    try {
      const [catalog, slotNow, cfg] = await Promise.all([api.catalog(), api.slot(), api.config()]);
      setProducts(catalog.products);
      setStale(catalog.stale);
      setSlot(slotNow);
      setConfig(cfg);
      // wholesale closed on the server: never leave the app stuck in that channel
      if (!cfg.wholesaleOpen) setChannel("retail");
      setError(undefined);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.code : "shop");
    } finally {
      setReady(true);
    }
  }, []);

  /**
   * Coming back to an order. A phone switching apps reloads the page all
   * the time; the order id lives in the hash and, failing that, in storage.
   */
  useEffect(() => {
    const id = orderIdFromHash() ?? persisted.current.lastOrderId;
    if (!id) return;
    let live = true;
    api
      .order(id)
      .then((found) => {
        if (!live) return;
        setOrder(found);
        const finished = found.stage === "delivered" || found.stage === "cancelled";
        if (orderIdFromHash() || !finished) setScreen("track");
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    void refresh();
    // keep the cutoff honest without hammering the server
    const timer = setInterval(() => void api.slot().then(setSlot).catch(() => {}), 60_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  /** Re-quote whenever the pin moves. Only the latest request may win. */
  useEffect(() => {
    if (!location || channel !== "retail") {
      setQuote(undefined);
      setQuoteError(undefined);
      return;
    }
    let live = true;
    setQuoting(true);
    api
      .quote(location)
      .then((q) => {
        if (!live) return;
        setQuote(q);
        setQuoteError(undefined);
      })
      .catch((caught: unknown) => {
        if (!live) return;
        setQuote(undefined);
        if (caught instanceof ApiError && caught.code === "outside_area") {
          const phone = (caught.detail as { officePhone?: string } | undefined)?.officePhone;
          setQuoteError(phone ? `outside:${phone}` : "outside:");
        } else {
          setQuoteError("failed");
        }
      })
      .finally(() => {
        if (live) setQuoting(false);
      });
    return () => {
      live = false;
    };
  }, [location, channel]);

  const totals = useMemo(
    () =>
      products.length
        ? priceCart({ lines, catalog: products, channel, km: quote?.km })
        : EMPTY_TOTALS,
    [lines, products, channel, quote],
  );

  const bump = useCallback(
    (product: Product, direction: 1 | -1) => {
      const result = bumpCart(lines, product, direction, channel);
      setLines(result.lines);
      setClampNotice(
        result.clampedTo === undefined ? undefined : `${result.clampedTo}|${product.code}`,
      );
    },
    [lines, channel],
  );

  const setQty = useCallback(
    (product: Product, qty: number) => setLines(setCartQty(lines, product, qty).lines),
    [lines],
  );

  const clear = useCallback(() => {
    setLines({});
    setOrder(undefined);
    setScreen("shop");
    if (typeof window !== "undefined") window.history.replaceState(null, "", window.location.pathname);
  }, []);

  /** A past order back in the basket, clamped to today's stock. */
  const reorder = useCallback(
    (past: Order) => {
      setLines(linesForReorder(past.lines, products));
      setScreen("cart");
      if (typeof window !== "undefined") window.history.replaceState(null, "", window.location.pathname);
    },
    [products],
  );

  /** An accepted order empties the basket, so a reload cannot place it twice. */
  const placed = useCallback((accepted: Order) => {
    setOrder(accepted);
    setLines({});
    setScreen("track");
    if (typeof window !== "undefined") window.history.replaceState(null, "", `#order/${accepted.id}`);
  }, []);

  /** Poll one order while the customer is watching it move. */
  useEffect(() => {
    if (screen !== "track" || !order) return;
    if (order.stage === "delivered" || order.stage === "cancelled") return;
    const id = order.id;
    const timer = setInterval(() => {
      void api
        .order(id)
        .then(setOrder)
        .catch(() => {});
      // quicker while the customer is staring at "waiting": the office answer should feel prompt
    }, order.stage === "placed" ? 8_000 : 15_000);
    return () => clearInterval(timer);
  }, [screen, order]);

  /** The customer cancels their own order; the server refuses once it is on the way. */
  const cancelOrder = useCallback(
    async (reason: CancelReason) => {
      if (!order) return;
      setOrder(await api.cancel(order.id, reason));
    },
    [order],
  );

  return {
    state: {
      ready,
      error,
      stale,
      products,
      config,
      slot,
      lines,
      channel,
      wholesaleCode,
      location,
      quote,
      quoteError,
      quoting,
      totals,
      screen,
      order,
      clampNotice,
    } satisfies AppState,
    actions: {
      bump,
      setQty,
      setChannel,
      setWholesaleCode,
      setLocation,
      placed,
      reorder,
      cancelOrder,
      setScreen,
      setOrder,
      clear,
      refresh,
      dismissClamp: () => setClampNotice(undefined),
    },
  };
}
